import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useV3Enabled } from '@/lib/v3-flag.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.jsx';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command.jsx';
import { searchV3, FIXTURE_BADGE } from '@/services/v3.service.js';
import { paletteOwner, V3_PALETTE } from '@/lib/palette-owner.js';
import { PHONE_QUERY } from '@/components/v3/PhoneLayout.jsx';
import useMediaQuery from '@/hooks/useMediaQuery.js';

// THE V3 Cmd+K (V3 overnight 1, Track C step 2). Opens on Cmd+K / Ctrl+K and
// on the TopBar search button, on every route while the v3 flag is on
// (lib/palette-owner.js names the owner from lib/v3-flag.js; with the flag off
// or not known the V2 SearchPalette keeps the keys). Every row lands where
// the search service's href says (the v3 account page). Built on
// ui/command.jsx. Calls searchV3 (docs/v3-account-api.md GET /api/v3/search)
// 200 ms after the last keystroke and renders the contract's rows grouped by
// account in the server's order: the type word, label, sublabel, and the
// matched field and value quietly at the right. The score is on the wire so
// the title attribute can say why a row sits where it does; nothing here
// sorts, scores or ranks. Enter navigates to the row's href; a policy hit
// carries ?term=<policy id> and the page scrolls that term open. Three empty
// states, three strings: under two characters, nothing matched, and search
// unavailable (a failed call is never "nothing matched"). On phone (under
// 640 px, useMediaQuery on PHONE_QUERY) the dialog is the whole screen and
// the list takes the height under the input.
// David's rulings of 2026-10-12 (services/v3/search.service.js): accounts with
// a live term come first and the 30 cap counts accounts. The server says which
// accounts have no live term (`live: false` on the row) and the heading says
// so in a word, NO_LIVE_TERM, so the order is never explained by position alone.

export const PLACEHOLDER = 'Search anything: N-number, pilot, account, policy number, phone';
export const EMPTY_TYPE_MORE = 'Type two characters to search';
export const EMPTY_NO_MATCH = 'Nothing matched';
export const EMPTY_UNAVAILABLE = 'Search is unavailable right now';
export const SEARCHING = 'Searching…';
export const NO_ACCOUNT = 'Not linked to an account';
export const NO_LIVE_TERM = 'no live term';
export const DEBOUNCE_MS = 200;
const MIN_CHARS = 2;
export const PHONE_DIALOG_CLASS = 'left-0 top-0 flex h-[100dvh] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0';

// The rows grouped by account_id in order of first appearance. The server
// already ordered them best first across and within accounts; the groups keep
// that order and the rows keep theirs.
export function groupByAccount(results) {
  const groups = new Map();
  for (const r of results || []) {
    const key = r.account_id ? `a:${r.account_id}` : `none:${r.type}:${r.id}`;
    if (!groups.has(key)) groups.set(key, { key, heading: r.account_id ? `${r.account_name || 'account'}${r.live === false ? ` · ${NO_LIVE_TERM}` : ''}` : NO_ACCOUNT, rows: [] });
    groups.get(key).rows.push(r);
  }
  return [...groups.values()];
}

export default function V3SearchPalette() {
  const owner = paletteOwner(useV3Enabled());
  const phone = useMediaQuery(PHONE_QUERY);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('idle');
  const [results, setResults] = useState([]);
  const [source, setSource] = useState('api');

  useEffect(() => {
    if (owner !== V3_PALETTE) return undefined;
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((o) => !o); }
    }
    function onCustom() { setOpen(true); }
    window.addEventListener('keydown', onKey);
    window.addEventListener('search-palette:open', onCustom);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('search-palette:open', onCustom);
    };
  }, [owner]);

  useEffect(() => {
    if (!open) { setQ(''); setResults([]); setStatus('idle'); setSource('api'); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (q.trim().length < MIN_CHARS) { setResults([]); setStatus('idle'); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setStatus('loading');
      try {
        const r = await searchV3(q);
        if (!alive) return;
        setResults(r.data?.results || []);
        setSource(r.source);
        setStatus(r.data?.ok === false ? 'unavailable' : 'done');
      } catch {
        if (!alive) return;
        setResults([]);
        setStatus('unavailable');
      }
    }, DEBOUNCE_MS);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  const groups = useMemo(() => groupByAccount(results), [results]);
  const go = (r) => { if (!r?.href) return; setOpen(false); navigate(r.href); };
  const empty = status === 'unavailable' ? EMPTY_UNAVAILABLE : status === 'idle' ? EMPTY_TYPE_MORE : status === 'loading' ? SEARCHING : EMPTY_NO_MATCH;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={phone ? PHONE_DIALOG_CLASS : 'max-w-xl p-0'} data-v3-palette data-phone={phone ? 'true' : 'false'}>
        <DialogTitle className="sr-only">Search</DialogTitle>
        <Command shouldFilter={false} loop>
          <CommandInput value={q} onValueChange={setQ} placeholder={PLACEHOLDER} autoFocus className="h-12 pr-10" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false" />
          {source === 'fixture' ? (
            <div className="flex items-center gap-2 border-b border-border bg-warning-soft px-3 py-1.5 text-xs text-warning" data-fixture-badge>
              <span className="rounded-pill bg-warning px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">{FIXTURE_BADGE}</span>
              <span>The v3 API answered disabled or did not answer; these rows are the contract fixture.</span>
            </div>
          ) : null}
          <CommandList className={phone ? 'max-h-none min-h-0 flex-1' : 'max-h-[60vh]'}>
            {groups.length === 0 ? <CommandEmpty data-empty-state={status}>{empty}</CommandEmpty> : null}
            {groups.map((g) => (
              <CommandGroup key={g.key} heading={g.heading}>
                {g.rows.map((r) => (
                  <CommandItem key={`${r.type}:${r.id}`} value={`${r.type}:${r.id}`} disabled={!r.href} onSelect={() => go(r)} title={r.score == null ? undefined : `score ${r.score}`} data-result-type={r.type}>
                    <span className="w-14 shrink-0 text-[10px] uppercase tracking-wide text-text-dim">{r.type}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-text">{r.label}</span>
                      {r.sublabel ? <span className="block truncate text-[11px] text-text-dim">{r.sublabel}</span> : null}
                    </span>
                    {r.match ? <span className="ml-auto hidden shrink-0 font-mono text-[10px] text-text-dim sm:inline" data-match-field={r.match.field}>{r.match.field} {r.match.value}</span> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
